"use client";
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useCategories } from "../context/CategoriesContext";
import { useCart } from "../context/CartContext";
import toast from 'react-hot-toast';

// Enhanced ProductCard component matching the design from ProductGrid
function EnhancedProductCard({ product = {} }) {
  const [isHovered, setIsHovered] = useState(false);
  const { categories = [] } = useCategories();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const { id, name, categoryId, price, offerPrice, image } = product;

  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[String(c.id)] = c.title;
    });
    return map;
  }, [categories]);

  // Determine if there's a valid offer/discount
  const hasValidOffer = offerPrice && price && parseFloat(offerPrice) < parseFloat(price);

  // Calculate discount percentage
  const calculateDiscount = () => {
    if (!hasValidOffer) return null;
    const original = parseFloat(price);
    const sale = parseFloat(offerPrice);
    return Math.round(((original - sale) / original) * 100);
  };

  const discountPercentage = calculateDiscount();

  // Determine display prices
  const displayPrice = hasValidOffer ? offerPrice : price;
  const originalDisplayPrice = hasValidOffer ? price : null;

  // Alternative images for hover effect
  const hoverImages = {
   
  };

  const currentImage = isHovered && hoverImages[image] ? hoverImages[image] : image;

  const handleCardClick = (e) => {
    // Don't navigate if clicking on the cart button
    if (e.target.closest('button[aria-label*="cart"]')) {
      return;
    }

    // Navigate to product page
    navigate(`/product/${id}`);
  };

  const handleCartClick = (e) => {
    e.stopPropagation();

    const user = JSON.parse(localStorage.getItem("user"));

    if (!user) {
      navigate("/signin");
      return;
    }

    try {
      // Add product to cart
      addToCart({
        id: id,
        name: name,
        price: displayPrice,
        image: image,
        categoryId: categoryId,
        quantity: 1
      });

      // Show success toast
      toast.success(`${name} added to cart!`, {
        icon: <ShoppingCart size={16} className="text-green-600" />,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    }
  };

  if (!id) return null; // safety guard

  return (
    <article
      onClick={handleCardClick}
      className="flex flex-col h-full p-4 mx-auto w-full bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group"
      role="article"
      aria-labelledby={`product-${id}-name`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Sale Ribbon */}
      {discountPercentage && (
        <div className="absolute top-0 left-4 z-20 transform translate-y-0">
          <div className="relative">
            {/* Main ribbon body */}
            <div className="relative w-12 bg-gradient-to-b from-red-500 via-red-600 to-red-700 shadow-2xl">
              {/* Top rounded cap */}
              <div className="w-full h-0 bg-gradient-to-b from-red-400 to-red-500 rounded-t-lg"></div>

              {/* Content area */}
              <div className="px-1 py-3 text-center relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="w-full h-full bg-gradient-to-br from-white via-transparent to-transparent"></div>
                </div>

                {/* Percentage number */}
                <div className="relative z-10 text-white font-black text-xl leading-none mb-1 drop-shadow-lg">
                  {discountPercentage}%
                </div>

                {/* OFF text */}
                <div className="relative z-10 text-white font-bold text-xs tracking-wider drop-shadow-md">
                  OFF
                </div>

                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 transform -skew-y-12 animate-shine-vertical"></div>
              </div>

              {/* V-shaped bottom */}
              <div className="relative">
                <div className="w-0 h-0 mx-auto border-l-8 border-r-8 border-t-6 border-l-red-700 border-r-red-700 border-t-red-700"></div>
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-6 border-r-6 border-t-4 border-l-red-800 border-r-red-800 border-t-red-800"></div>
              </div>

              {/* Side shadows for 3D effect */}
              <div className="absolute inset-y-0 -left-1 w-1 bg-gradient-to-b from-red-800 to-red-900 opacity-60 rounded-l-sm"></div>
              <div className="absolute inset-y-0 -right-1 w-1 bg-gradient-to-b from-red-300 to-red-400 opacity-40 rounded-r-sm"></div>
            </div>

            {/* Outer glow */}
            <div className="absolute inset-0 bg-red-500 opacity-30 blur-lg scale-100 rounded-lg"></div>

            {/* Floating sparkles */}
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-300 rounded-full animate-ping opacity-75"></div>
            <div className="absolute top-6 -left-2 w-1.5 h-1.5 bg-yellow-200 rounded-full animate-pulse opacity-60"></div>
          </div>
        </div>
      )}

      {/* Image Container */}
      <div className="relative overflow-hidden mb-4 rounded-lg">
        <img
          src={currentImage}
          alt={name}
          className="object-contain w-full aspect-[0.85] transition-all duration-500 group-hover:scale-105"
          loading="lazy"
        />

        {/* Cart Icon - Top Right */}
        <button
          onClick={handleCartClick}
          className="absolute top-1 right-1 z-10 w-10 h-10 bg-black hover:bg-gray-800 flex items-center justify-center shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-110 group/cart"
          aria-label={`Add ${name} to cart`}
        >
          <ShoppingCart
            size={22}
            className="text-white group-hover/cart:text-gray-200 transition-colors duration-200"
          />
        </button>

        {/* Overlay effect on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-300 rounded-lg"></div>
      </div>

      {/* Content Container */}
      <div className="flex flex-col flex-1 justify-between">
        {/* Title and Price Section */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3
            id={`product-${id}-name`}
            className="flex-1 text-base font-semibold text-black capitalize group-hover:text-zinc-700 transition-colors duration-200 truncate leading-tight"
            title={name}
          >
            {name}
          </h3>
          <div className="flex items-end gap-2 flex-shrink-0">
            <span
              className={`text-lg font-bold transition-colors duration-200 ${
                hasValidOffer
                  ? 'text-red-500 group-hover:text-red-600'
                  : 'text-black group-hover:text-zinc-700'
              }`}
              aria-label={`${hasValidOffer ? 'Sale' : 'Current'} price ₹${displayPrice}`}
            >
              ₹{displayPrice}
            </span>
            {originalDisplayPrice && (
              <span
                className="text-md text-gray-500 line-through"
                aria-label={`Original price ₹${originalDisplayPrice}`}
              >
                ₹{originalDisplayPrice}
              </span>
            )}
          </div>
        </div>

        {/* Category Section */}
        <div>
          <p className="text-xs text-gray-500 truncate">
            {categoryMap[String(categoryId)] || "Uncategorized"}
          </p>
           {/* Show minimum order quantity if greater than 1 */}
            {(product?.minimumOrderQty || product?.min_order_qty || 1) > 1 && (
              <p className="text-xs text-blue-600 font-medium">
                Min. order: {product?.minimumOrderQty || product?.min_order_qty || 1} units
              </p>
            )}
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes shine-vertical {
          0% { transform: translateY(-100%) skewY(-12deg); }
          100% { transform: translateY(200%) skewY(-12deg); }
        }

        .animate-shine-vertical {
          animation: shine-vertical 3s infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }

        .animate-float {
          animation: float 2s ease-in-out infinite;
        }
      `}</style>
    </article>
  );
}

function ProductCarousel({ title, products }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [scrollStart, setScrollStart] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);

  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const total = products.length;
  const safeTotal = Math.max(total, 1);
  const [visibleItems, setVisibleItems] = useState(4);

  const getItemWidth = () => {
    if (!scrollRef.current?.children[0]) return 320;
    return scrollRef.current.children[0].offsetWidth + 20;
  };

  const maxIndex = Math.max(0, total - visibleItems);

  // Update scroll state
  const updateScrollState = useCallback(() => {
    if (scrollRef.current) {
      const itemWidth = getItemWidth();
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      setCurrentIndex(Math.round(scrollLeft / itemWidth));
    }
  }, []);

  const scrollToIndex = useCallback(
    (index) => {
      if (scrollRef.current && !isScrolling) {
        setIsScrolling(true);

        const itemWidth = getItemWidth();
        const targetScroll = index * itemWidth;

        scrollRef.current.scrollTo({
          left: targetScroll,
          behavior: "smooth",
        });

        setTimeout(() => {
          setIsScrolling(false);
          updateScrollState();
        }, 500);
      }
    },
    [isScrolling, updateScrollState]
  );

  // Navigation functions
  const scrollLeft = useCallback(() => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToIndex(newIndex);
  }, [currentIndex, scrollToIndex]);

  const scrollRight = useCallback(() => {
    const newIndex = Math.min(maxIndex, currentIndex + 1);
    scrollToIndex(newIndex);
  }, [currentIndex, maxIndex, scrollToIndex]);

  // Improved drag handlers
  const handleDragStart = useCallback((clientX) => {
    if (scrollRef.current) {
      setIsDragging(true);
      setHasDragged(false);
      setDragStart(clientX);
      setScrollStart(scrollRef.current.scrollLeft);

      // Disable text selection during drag
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    }
  }, []);

  const handleDragMove = useCallback((clientX) => {
    if (isDragging && scrollRef.current) {
      const diff = dragStart - clientX;

      // Only start dragging if moved more than 5px to avoid accidental drags
      if (Math.abs(diff) > 5) {
        setHasDragged(true);
        scrollRef.current.scrollLeft = scrollStart + diff;
      }
    }
  }, [isDragging, dragStart, scrollStart]);

  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);

      // Re-enable text selection
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';

      updateScrollState();

      // Reset drag state after a short delay
      setTimeout(() => {
        setHasDragged(false);
      }, 100);
    }
  }, [isDragging, updateScrollState]);

  // Mouse events with improved handling
  const handleMouseDown = useCallback((e) => {
    // Only handle left mouse button and not on product cards
    if (e.button !== 0 || e.target.closest('article')) return;

    e.preventDefault();
    e.stopPropagation();
    handleDragStart(e.clientX);
  }, [handleDragStart]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      handleDragMove(e.clientX);
    }
  }, [isDragging, handleDragMove]);

  const handleMouseUp = useCallback((e) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      handleDragEnd();
    }
  }, [isDragging, handleDragEnd]);

  // Touch events with improved handling
  const handleTouchStart = useCallback((e) => {
    // Don't prevent default for touch to allow normal scrolling
    // Only handle if not touching a product card
    if (e.touches.length === 1 && !e.target.closest('article')) {
      handleDragStart(e.touches[0].clientX);
    }
  }, [handleDragStart]);

  const handleTouchMove = useCallback((e) => {
    if (isDragging && e.touches.length === 1) {
      // Only prevent default if we're actually dragging
      if (hasDragged) {
        e.preventDefault();
      }
      handleDragMove(e.touches[0].clientX);
    }
  }, [isDragging, hasDragged, handleDragMove]);

  const handleTouchEnd = useCallback((e) => {
    if (isDragging) {
      e.preventDefault();
      handleDragEnd();
    }
  }, [isDragging, handleDragEnd]);

  // Handle clicks on draggable area
  const handleClick = useCallback((e) => {
    // Prevent clicks if we just finished dragging, but not on product cards
    if (hasDragged && !e.target.closest('article')) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [hasDragged]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollLeft();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollRight();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [scrollLeft, scrollRight]);

  useEffect(() => {
    const updateVisibleItems = () => {
      if (window.innerWidth < 640) setVisibleItems(1);
      else if (window.innerWidth < 1024) setVisibleItems(2);
      else setVisibleItems(4);
    };

    updateVisibleItems();
    window.addEventListener("resize", updateVisibleItems);
    return () => window.removeEventListener("resize", updateVisibleItems);
  }, []);

  // Global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp, { passive: false });

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Initialize scroll state
  useEffect(() => {
    updateScrollState();
  }, [updateScrollState]);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    if (!isScrolling && !isDragging) {
      updateScrollState();
    }
  }, [isScrolling, isDragging, updateScrollState]);

  if (!products || products.length === 0) {
    return (
      <section className="py-10 text-center text-gray-500">
        No products available
      </section>
    );
  }

  return (
    <section
      className="w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="region"
      aria-label={`${title} product carousel`}
    >
      {/* Header with Navigation */}
      <header className="flex justify-between items-center mb-6 w-full">
        <h2 className="text-4xl font-bold tracking-wide leading-10 text-teal-700 max-sm:text-2xl">
          {title}
        </h2>

        {/* Navigation Controls */}
        <nav className="flex gap-4 items-center" aria-label="Carousel navigation">
          <button
            className={`flex justify-center items-center h-10 w-10 cursor-pointer rounded-full border-2 border-black transition-all duration-300 hover:bg-teal-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 ${
              !canScrollLeft ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            aria-label="Previous products"
          >
            <svg width="18" height="10" viewBox="0 0 18 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.00085 8.89984C4.81332 9.08731 4.55901 9.19263 4.29385 9.19263C4.02869 9.19263 3.77438 9.08731 3.58685 8.89984L0.394849 5.70684C0.301873 5.61397 0.228114 5.50368 0.177789 5.38228C0.127465 5.26089 0.101562 5.13076 0.101563 4.99934C0.101562 4.86793 0.127465 4.7378 0.177789 4.6164C0.228114 4.495 0.301873 4.38472 0.394849 4.29184L3.58585 1.09984C3.77336 0.912203 4.02773 0.806734 4.293 0.806641C4.55827 0.806547 4.81271 0.911835 5.00035 1.09934C5.18799 1.28685 5.29346 1.54122 5.29355 1.80649C5.29365 2.07176 5.18836 2.3262 5.00085 2.51384L3.51585 3.99984H17.0008C17.2661 3.99984 17.5204 4.1052 17.708 4.29274C17.8955 4.48027 18.0008 4.73463 18.0008 4.99984C18.0008 5.26506 17.8955 5.51941 17.708 5.70695C17.5204 5.89449 17.2661 5.99984 17.0008 5.99984H3.51585L5.00085 7.48484C5.09383 7.57772 5.16758 7.688 5.21791 7.8094C5.26823 7.9308 5.29414 8.06093 5.29414 8.19234C5.29414 8.32376 5.26823 8.45389 5.21791 8.57528C5.16758 8.69668 5.09383 8.80697 5.00085 8.89984V8.89984Z" fill="currentColor"/>
            </svg>
          </button>

          <button
            className={`flex justify-center items-center h-10 w-10 cursor-pointer rounded-full border-2 border-black transition-all duration-300 hover:bg-teal-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 ${
              !canScrollRight ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={scrollRight}
            disabled={!canScrollRight}
            aria-label="Next products"
          >
            <svg width="18" height="10" viewBox="0 0 18 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.9992 1.10016C13.1867 0.912686 13.441 0.80737 13.7062 0.80737C13.9713 0.80737 14.2256 0.912686 14.4132 1.10016L17.6052 4.29316C17.6981 4.38603 17.7719 4.49632 17.8222 4.61772C17.8725 4.73911 17.8984 4.86924 17.8984 5.00066C17.8984 5.13207 17.8725 5.2622 17.8222 5.3836C17.7719 5.505 17.6981 5.61528 17.6052 5.70816L14.4142 8.90016C14.2266 9.0878 13.9723 9.19327 13.707 9.19336C13.4417 9.19345 13.1873 9.08816 12.9997 8.90066C12.812 8.71315 12.7065 8.45878 12.7064 8.19351C12.7064 7.92824 12.8116 7.6738 12.9992 7.48616L14.4842 6.00016H0.999151C0.733935 6.00016 0.47958 5.8948 0.292044 5.70726C0.104507 5.51973 -0.00084877 5.26537 -0.00084877 5.00016C-0.00084877 4.73494 0.104507 4.48059 0.292044 4.29305C0.47958 4.10551 0.733935 4.00016 0.999151 4.00016H14.4842L12.9992 2.51516C12.9062 2.42228 12.8324 2.312 12.7821 2.1906C12.7318 2.0692 12.7059 1.93907 12.7059 1.80766C12.7059 1.67624 12.7318 1.54611 12.7821 1.42472C12.8324 1.30332 12.9062 1.19303 12.9992 1.10016V1.10016Z" fill="currentColor"/>
            </svg>
          </button>
        </nav>
      </header>

      {/* Products Container */}
      <div ref={containerRef} className="relative w-full">
        <div
          ref={scrollRef}
          className={`flex gap-5 overflow-x-auto scrollbar-hide pb-4 ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
          onScroll={handleScroll}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={handleClick}
          role="list"
          aria-label={`${title} products`}
        >
          {products.map((product, index) => (
            <div
              key={product.id}
              className="flex-shrink-0 w-[300px] max-md:w-[280px] max-sm:w-full max-sm:max-w-[300px]"
              role="listitem"
            >
              <EnhancedProductCard product={product} />
            </div>
          ))}
        </div>

        {/* Custom Scroll Indicator */}
        <div className="relative w-full mt-4">
          <div className="w-full h-1 bg-teal-400 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-800 rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${Math.min(((currentIndex + visibleItems) / safeTotal) * 100, 100)}%`,
                transform: `translateX(${(currentIndex / safeTotal) * 100}%)`
              }}
            />
          </div>

          {/* Progress indicators */}
          <div className="flex justify-between mt-2 px-1">
            <span className="text-xs text-gray-500 font-medium">
              {currentIndex + 1} - {Math.min(currentIndex + visibleItems, products.length)} of {products.length}
            </span>
            <span className="text-xs text-gray-500">
              {Math.round(((currentIndex + visibleItems) / products.length) * 100)}%
            </span>
          </div>
        </div>

        {/* Gradient overlays for visual indication of scrollable content */}
        {canScrollLeft && (
          <div className="absolute left-0 top-0 w-8 h-full bg-gradient-to-r from-white to-transparent pointer-events-none z-10" />
        )}
        {canScrollRight && (
          <div className="absolute right-0 top-0 w-8 h-full bg-gradient-to-l from-white to-transparent pointer-events-none z-10" />
        )}
      </div>

      {/* Accessibility announcements */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        Showing products {currentIndex + 1} to {Math.min(currentIndex + visibleItems, products.length)} of {products.length}
      </div>

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}

export default ProductCarousel;
